import { LANGUAGES, type Language } from '@ps/constants';
import {
  Avatar,
  BottomNav,
  NoIndex,
  MenuButton,
  NavDrawer,
  OfflineBanner,
  SideNav,
  buttonClassName,
  type NavItem,
  type NavSection,
} from '@ps/ui';
import { useQuery } from '@tanstack/react-query';
import {
  Bell,
  Home,
  MessageCircle,
  Navigation,
  Search,
  Settings,
  ShieldAlert,
  ShieldCheck,
  Ticket,
  User,
} from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../features/auth/store';
import { fetchUnreadCount, notificationKeys } from '../features/notifications/api';
import { setLanguage } from '../i18n';

/**
 * The frame every customer screen renders inside.
 *
 * One list of destinations, shown where the device has room for it: a column
 * beside the content from `lg` up, and the same list behind the menu button
 * below that.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const current = (i18n.language === 'hi' ? LANGUAGES.HI : LANGUAGES.EN) as Language;

  const [menuOpen, setMenuOpen] = useState(false);

  const status = useAuthStore((state) => state.status);
  const user = useAuthStore((state) => state.user);
  const restore = useAuthStore((state) => state.restore);

  // Check the stored session once, on load, so the header settles immediately
  // rather than flickering between "Sign in" and the avatar.
  useEffect(() => {
    if (status === 'unknown') void restore();
  }, [status, restore]);

  // Polled rather than pushed: one small request a minute is cheaper for a
  // phone on a weak connection than a socket that must be kept alive.
  const unread = useQuery({
    queryKey: notificationKeys.unread,
    queryFn: fetchUnreadCount,
    enabled: status === 'authenticated',
    refetchInterval: 60_000,
  });

  const item = (
    key: string,
    label: string,
    icon: ReactNode,
    to: string,
    matches: (path: string) => boolean,
  ): NavItem => ({
    key,
    label,
    icon,
    isActive: matches(location.pathname),
    onSelect: () => navigate(to),
  });

  const signedIn = status === 'authenticated';

  // The five the passenger reaches for, in the bottom bar on a phone and at the
  // top of the sidebar on a desktop.
  const primary: NavItem[] = [
    item('home', t('nav.home'), <Home className="h-5 w-5" />, '/', (path) => path === '/'),
    item('search', t('nav.search'), <Search className="h-5 w-5" />, '/search', (path) =>
      path.startsWith('/search'),
    ),
    item(
      'bookings',
      t('nav.bookings'),
      <Ticket className="h-5 w-5" />,
      '/bookings',
      (path) => path.startsWith('/bookings') && !path.endsWith('/chat'),
    ),
    item(
      'messages',
      t('nav.messages'),
      <MessageCircle className="h-5 w-5" />,
      '/messages',
      (path) => path.startsWith('/messages') || path.endsWith('/chat'),
    ),
    item(
      'profile',
      t('nav.profile'),
      <User className="h-5 w-5" />,
      signedIn ? '/profile' : '/login',
      (path) => path.startsWith('/profile'),
    ),
  ];

  const sections: NavSection[] = [
    { items: primary },
    {
      label: t('nav.account'),
      items: [
        item('trips', t('nav.trips'), <Navigation className="h-5 w-5" />, '/trips', (path) =>
          path.startsWith('/trips'),
        ),
        item(
          'notifications',
          t('nav.notifications'),
          <Bell className="h-5 w-5" />,
          '/notifications',
          (path) => path.startsWith('/notifications'),
        ),
        item(
          'security',
          t('nav.security'),
          <ShieldCheck className="h-5 w-5" />,
          '/security',
          (path) => path.startsWith('/security'),
        ),
        item('safety', t('nav.safety'), <ShieldAlert className="h-5 w-5" />, '/safety', (path) =>
          path.startsWith('/safety'),
        ),
        item('settings', t('nav.settings'), <Settings className="h-5 w-5" />, '/settings', (path) =>
          path.startsWith('/settings'),
        ),
      ],
    },
  ];

  // Everything that is not one of these needs a sign-in, and a signed-in screen
  // has nothing to offer a search engine. Marked here rather than page by page,
  // so a new private screen is private by default.
  const publicPaths = [
    '/',
    '/how-it-works',
    '/routes',
    '/safety-info',
    '/faq',
    '/contact',
    '/terms',
    '/privacy',
    '/cancellation-policy',
    '/refund-policy',
    '/community-guidelines',
    '/safety-policy',
    '/cookie-policy',
    '/disclaimer',
    '/grievance',
  ];
  const isPublic =
    publicPaths.includes(location.pathname) ||
    // Anything the console publishes lives under /legal/<slug>, and a policy
    // behind a sign-in is not a policy anybody agreed to.
    location.pathname.startsWith('/legal/') ||
    location.pathname.endsWith('-carpool');

  // The sign-in screens are already the sign-in call to action; a second one in
  // the header competes with the form it would send you to.
  const onSignInRoute = ['/login', '/verify-otp'].includes(location.pathname);

  const brand = (
    <Link to="/" className="flex shrink-0 items-center gap-2" onClick={() => setMenuOpen(false)}>
      <span
        aria-hidden="true"
        className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-base font-bold text-on-primary shadow-brand"
      >
        PS
      </span>
      <span className="ps-subheading text-navy">{t('app.name')}</span>
    </Link>
  );

  const languageChooser = (
    <label className="flex items-center gap-2">
      <span className="sr-only">{t('common.language')}</span>
      <select
        value={current}
        onChange={(event) => setLanguage(event.target.value as Language)}
        className="h-9 rounded-full border border-border bg-background px-3 text-sm font-medium text-content-secondary transition-colors hover:border-primary-300"
      >
        <option value={LANGUAGES.EN}>English</option>
        <option value={LANGUAGES.HI}>हिंदी</option>
      </select>
    </label>
  );

  return (
    <div className="flex min-h-dvh flex-col bg-surface">
      {!isPublic && <NoIndex />}

      <OfflineBanner message={`${t('common.offlineTitle')} ${t('common.offlineBody')}`} />

      <header className="sticky top-0 z-30 border-b border-border/70 bg-background/85 backdrop-blur-md">
        <div className="ps-container flex h-16 items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-1">
            {/* Nothing behind the menu belongs to somebody who is not signed in,
                so they get the brand and a way in instead of an empty list. */}
            {signedIn && (
              <MenuButton
                open={menuOpen}
                onToggle={() => setMenuOpen((value) => !value)}
                label={t('nav.menu')}
              />
            )}
            {brand}
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <span className="hidden sm:inline">{languageChooser}</span>

            {signedIn ? (
              <>
                <Link
                  to="/notifications"
                  aria-label={t('notifications.title')}
                  className="relative flex h-11 w-11 items-center justify-center rounded-full text-content-muted transition-colors hover:bg-surface hover:text-primary"
                >
                  <Bell className="h-5 w-5" />
                  {(unread.data ?? 0) > 0 && (
                    <span className="absolute right-1.5 top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-on-primary">
                      {unread.data}
                    </span>
                  )}
                </Link>
                <Link to="/profile" aria-label={t('profile.editProfile')}>
                  <Avatar name={user?.displayName} src={user?.photoUrl} size="sm" />
                </Link>
              </>
            ) : (
              !onSignInRoute && (
                <Link to="/login" className={buttonClassName({ size: 'sm' })}>
                  {t('auth.signIn')}
                </Link>
              )
            )}
          </div>
        </div>
      </header>

      <div className="flex flex-1">
        {signedIn && <SideNav sections={sections} footer={languageChooser} />}
        {/* The offset keeps the last thing on a page clear of the bottom bar. */}
        <main className={`min-w-0 flex-1 ${signedIn ? 'ps-bottom-nav-offset' : ''}`}>
          {children}
        </main>
      </div>

      {/* The public and legal pages are only reachable from here, so the
          footer is not decoration: without it they are written and unreachable.
          Cashfree checks for exactly these pages on a live site. */}
      <footer className="ps-bleed border-t border-border bg-background">
        <div className="ps-container py-10">
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <p className="ps-subheading text-navy">{t('app.name')}</p>
              <p className="ps-caption mt-1.5">{t('public.footerTagline')}</p>
              <p className="ps-caption mt-3">{t('legal.brandLine')}</p>
            </div>

            <nav aria-label={t('public.footerProduct')}>
              <p className="ps-small mb-2.5 font-semibold text-navy">{t('public.footerProduct')}</p>
              <ul className="flex flex-col gap-2">
                {[
                  { to: '/search', label: t('public.findRide') },
                  { to: '/routes', label: t('public.routes') },
                  { to: '/how-it-works', label: t('public.howItWorks') },
                  { to: '/safety-info', label: t('public.safety') },
                ].map((link) => (
                  <li key={link.to}>
                    <Link
                      to={link.to}
                      className="ps-caption text-content-secondary transition-colors hover:text-primary"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>

            <nav aria-label={t('public.footerCompany')}>
              <p className="ps-small mb-2.5 font-semibold text-navy">{t('public.footerCompany')}</p>
              <ul className="flex flex-col gap-2">
                {[
                  { to: '/faq', label: t('public.faq') },
                  { to: '/contact', label: t('public.contact') },
                  { to: '/community-guidelines', label: t('public.guidelines') },
                ].map((link) => (
                  <li key={link.to}>
                    <Link
                      to={link.to}
                      className="ps-caption text-content-secondary transition-colors hover:text-primary"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>

            <nav aria-label={t('public.footerLegalGroup')}>
              <p className="ps-small mb-2.5 font-semibold text-navy">
                {t('public.footerLegalGroup')}
              </p>
              <ul className="flex flex-col gap-2">
                {[
                  { to: '/terms', label: t('public.terms') },
                  { to: '/privacy', label: t('public.privacy') },
                  { to: '/cancellation-policy', label: t('public.cancellation') },
                  { to: '/refund-policy', label: t('public.refund') },
                  { to: '/safety-policy', label: t('public.safetyPolicy') },
                  { to: '/cookie-policy', label: t('public.cookiePolicy') },
                  { to: '/disclaimer', label: t('public.disclaimer') },
                  { to: '/grievance', label: t('public.grievance') },
                ].map((link) => (
                  <li key={link.to}>
                    <Link
                      to={link.to}
                      className="ps-caption text-content-secondary transition-colors hover:text-primary"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          </div>

          {/* The operator, named in the middle where a reader looks for it. A
              brand that does not say who runs it fails the first check a
              payment gateway makes. */}
          <div className="mt-10 border-t border-border pt-6 text-center">
            <p className="ps-small font-semibold text-navy">{t('legal.managedBy')}</p>
            {/* A reader — and every payment gateway's reviewer — should be able
                to reach the operator without hunting for a contact page. */}
            <p className="ps-caption mt-1.5">
              <a href="mailto:hello@wevantainfotech.com" className="hover:text-primary">
                hello@wevantainfotech.com
              </a>
              {' · '}
              <a href="tel:+919648633351" className="hover:text-primary">
                +91 96486 33351
              </a>
            </p>
            <p className="ps-caption mt-1.5">
              © {new Date().getFullYear()} {t('legal.operator')}. {t('public.rights')}
            </p>
          </div>
        </div>
      </footer>

      {/* Five destinations at the thumb, and the rest behind the menu button. */}
      {signedIn && <BottomNav items={primary} />}

      <NavDrawer
        open={menuOpen && signedIn}
        onClose={() => setMenuOpen(false)}
        sections={sections}
        header={brand}
        footer={
          <div className="flex items-center justify-between gap-3">
            {languageChooser}
            {signedIn && <Avatar name={user?.displayName} src={user?.photoUrl} size="sm" />}
          </div>
        }
      />
    </div>
  );
}
